/**
 * BOGO Builder - Main JavaScript
 * Biktrix Valentine's Day BOGO Campaign
 */

(function() {
  'use strict';

  // ===== STATE =====
  let currentStep = 1;
  let selectedBikes = [null, null];
  let selectedAccessories = [];
  let selectedProtection = [];

  // ===== INITIALIZATION =====
  document.addEventListener('DOMContentLoaded', function() {
    initBOGOBuilder();
  });

  function initBOGOBuilder() {
    // Calculate and set header height for sticky positioning
    setHeaderHeight();
    window.addEventListener('resize', debounce(setHeaderHeight, 100));

    // Initialize progress bar
    updateProgressBar();

    // Initialize sticky bar
    updateStickyBar();

    // Initialize protection checkbox listeners
    initProtectionCheckboxes();

    // Initialize product card button listeners (for theme-styled cards)
    initProductCardButtons();

    // Initialize accessory button listeners
    initAccessoryButtons();

    // Initialize quantity button handlers
    initQuantityButtons();

    // Show first step
    bogoGoToStep(1);
  }

  // ===== HEADER HEIGHT =====
  function setHeaderHeight() {
    let totalHeight = 0;

    // Try multiple selectors to find the site header
    const headerSelectors = [
      '.shopify-section-header',
      'header.section-header',
      '.header-section',
      '.site-header',
      '#shopify-section-header',
      'header[role="banner"]',
      '.header-wrapper'
    ];

    let header = null;
    for (const selector of headerSelectors) {
      header = document.querySelector(selector);
      if (header) break;
    }

    if (header) {
      const headerStyles = getComputedStyle(header);
      // Check if header is sticky or fixed
      if (headerStyles.position === 'sticky' || headerStyles.position === 'fixed') {
        totalHeight = header.getBoundingClientRect().height;
      }
    }

    // Also check for sticky elements that are currently at the top
    document.querySelectorAll('header, [class*="header"], [class*="announcement"]').forEach(function(el) {
      if (el.classList.contains('bogo-progress-bar')) return;
      const styles = getComputedStyle(el);
      if (styles.position === 'sticky' || styles.position === 'fixed') {
        const rect = el.getBoundingClientRect();
        // If element is at the top of viewport, it's likely a sticky header
        if (rect.top >= -5 && rect.top <= 5) {
          totalHeight = Math.max(totalHeight, rect.height);
        }
      }
    });

    // Only set the variable if we found a sticky header
    if (totalHeight > 0) {
      document.documentElement.style.setProperty('--header-height', totalHeight + 'px');
    } else {
      // Remove the variable so progress bar defaults to top: 0
      document.documentElement.style.removeProperty('--header-height');
    }
  }

  function debounce(func, wait) {
    let timeout;
    return function() {
      clearTimeout(timeout);
      timeout = setTimeout(func, wait);
    };
  }

  // ===== PRODUCT CARD BUTTONS =====
  function initProductCardButtons() {
    // Use event delegation for all grid interactions (bikes and accessories)
    document.addEventListener('click', function(e) {
      // Handle "Add to Bundle" button clicks
      const addBtn = e.target.closest('[data-action="add-to-bundle"]');
      if (addBtn) {
        const productCard = addBtn.closest('product-card');
        const bikeGrid = addBtn.closest('.bogo-bike-grid');
        const accessoryGrid = addBtn.closest('.bogo-accessories-grid');

        // Handle bike grid clicks
        if (bikeGrid && productCard) {
          e.preventDefault();
          e.stopPropagation();

          const step = productCard.dataset.bogoStep ? parseInt(productCard.dataset.bogoStep) :
                       (bikeGrid.dataset.step ? parseInt(bikeGrid.dataset.step) : 1);

          handleProductCardSelection(step, productCard);
        }
        // Handle accessory grid clicks
        else if (accessoryGrid && productCard) {
          e.preventDefault();
          e.stopPropagation();

          handleAccessoryCardSelection(productCard, addBtn);
        }
      }
    });

    // Handle variant dropdown changes
    document.addEventListener('change', function(e) {
      if (e.target.matches('[data-action="variant-change"]')) {
        const select = e.target;
        const productCard = select.closest('product-card');
        const selectedOption = select.options[select.selectedIndex];

        if (productCard && selectedOption) {
          updateProductCardVariant(productCard, selectedOption);
        }
      }
    });

    // Handle color swatch changes
    document.addEventListener('change', function(e) {
      if (e.target.matches('.color-swatch__radio')) {
        const radio = e.target;
        const productCard = radio.closest('product-card');

        if (productCard) {
          updateProductCardFromSwatch(productCard, radio);
        }
      }
    });
  }

  // Update product card when variant is changed via dropdown
  function updateProductCardVariant(productCard, selectedOption) {
    const variantId = selectedOption.dataset.id;
    const price = selectedOption.dataset.price;
    const mediaUrl = selectedOption.dataset.mediaUrl;
    const backdrop = selectedOption.dataset.backdrop;

    // Update card data attributes
    productCard.dataset.variantId = variantId;
    productCard.dataset.productPrice = price;

    // Update image
    const img = productCard.querySelector('.product-item__variant-image');
    if (img && mediaUrl) {
      img.src = mediaUrl;
    }

    // Update backdrop color
    const mediaDiv = productCard.querySelector('.product-item__media');
    if (mediaDiv && backdrop) {
      mediaDiv.className = mediaDiv.className.replace(/\b(white|gray|black|blue|red|green)\b/g, '');
      mediaDiv.classList.add(backdrop);
    }

    // Update price display
    const priceEl = productCard.querySelector('[data-price]');
    if (priceEl && price) {
      const formattedPrice = (parseInt(price) / 100).toLocaleString('en-US', { style: 'currency', currency: 'CAD' }).replace('.00', '');
      priceEl.innerHTML = '<span class="visually-hidden">Sale price</span>' + formattedPrice;
    }

    // Update button data-id
    const btn = productCard.querySelector('[data-action="add-to-bundle"]');
    if (btn) {
      btn.dataset.id = variantId;
    }
  }

  // Update product card when color swatch is selected
  function updateProductCardFromSwatch(productCard, radio) {
    const variantId = radio.dataset.variantId;
    const price = radio.dataset.price;
    const mediaUrl = radio.dataset.mediaUrl;
    const backdrop = radio.dataset.backdrop;

    // Update card data attributes
    if (variantId) productCard.dataset.variantId = variantId;
    if (price) productCard.dataset.productPrice = price;

    // Update image
    const img = productCard.querySelector('.product-item__variant-image');
    if (img && mediaUrl) {
      img.src = mediaUrl;
    }

    // Update backdrop color
    const mediaDiv = productCard.querySelector('.product-item__media');
    if (mediaDiv && backdrop) {
      mediaDiv.className = mediaDiv.className.replace(/\b(white|gray|black|blue|red|green)\b/g, '');
      mediaDiv.classList.add(backdrop);
    }

    // Update price display
    const priceEl = productCard.querySelector('[data-price]');
    if (priceEl && price) {
      const formattedPrice = (parseInt(price) / 100).toLocaleString('en-US', { style: 'currency', currency: 'CAD' }).replace('.00', '');
      priceEl.innerHTML = '<span class="visually-hidden">Sale price</span>' + formattedPrice;
    }

    // Update button data-id
    const btn = productCard.querySelector('[data-action="add-to-bundle"]');
    if (btn && variantId) {
      btn.dataset.id = variantId;
    }

    // Update the variant dropdown to match if it exists
    const select = productCard.querySelector('[data-action="variant-change"]');
    if (select) {
      const options = select.options;
      for (let i = 0; i < options.length; i++) {
        if (options[i].dataset.id === variantId) {
          select.selectedIndex = i;
          break;
        }
      }
    }
  }

  // Expose so QV "Select This Bike" can call it directly with an explicit step number
  window.bogoHandleCardSelection = function(step, card) {
    handleProductCardSelection(step, card);
  };

  function handleProductCardSelection(bikeNum, cardElement) {
    // Get current price from data attribute (may have been updated by variant/swatch selection)
    const currentPrice = parseInt(cardElement.dataset.productPrice) || 0;

    // Get current variant ID - prefer dropdown's selected option (most reliable after swatch changes,
    // since theme JS syncs the dropdown visually but doesn't dispatch a change event to update the button)
    const btn = cardElement.querySelector('[data-action="add-to-bundle"]');
    const variantSelect = cardElement.querySelector('[data-action="variant-change"]');
    let variantId;
    if (variantSelect && variantSelect.selectedIndex >= 0) {
      variantId = variantSelect.options[variantSelect.selectedIndex].dataset.id;
    }
    if (!variantId) {
      variantId = btn ? btn.dataset.id : (cardElement.dataset.variantId || cardElement.dataset.productId);
    }

    // Get current image (may have been updated)
    const currentImg = cardElement.querySelector('.product-item__variant-image');
    const imgSrc = currentImg ? currentImg.src : cardElement.dataset.productImage;

    // Get variant name (color/size) from dropdown or swatch
    let variantName = '';
    if (variantSelect && variantSelect.selectedIndex >= 0) {
      variantName = variantSelect.options[variantSelect.selectedIndex].textContent.trim();
    } else {
      // Try to get from checked swatch
      const checkedSwatch = cardElement.querySelector('.color-swatch__radio:checked');
      if (checkedSwatch) {
        const label = cardElement.querySelector('.color-swatch__label[for="' + checkedSwatch.id + '"]');
        if (label) {
          variantName = label.getAttribute('title') || label.textContent.trim();
        }
      }
    }

    // Get selected addons (BiktrixCare+, Theftcare)
    const addons = [];
    cardElement.querySelectorAll('.bogo-addon-checkbox:checked').forEach(function(checkbox) {
      addons.push({
        type: checkbox.dataset.addonType,
        id: checkbox.dataset.id,
        price: parseInt(checkbox.dataset.price) || 0
      });
    });

    // Get shipping ETA — prefer the selected dropdown option's data-availability (most accurate
    // for the actual chosen variant), then fall back to color swatch, then visible shipping text
    let shippingEta = '';
    if (variantSelect && variantSelect.selectedIndex >= 0) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (selectedOption && selectedOption.dataset.availability) {
        shippingEta = selectedOption.dataset.availability;
      }
    }
    if (!shippingEta) {
      const checkedSwatch = cardElement.querySelector('.color-swatch__radio:checked');
      if (checkedSwatch && checkedSwatch.dataset.availability) {
        shippingEta = checkedSwatch.dataset.availability;
      }
    }
    if (!shippingEta) {
      const shippingEl = cardElement.querySelector('.shipping-info_shipping-time');
      if (shippingEl) shippingEta = shippingEl.textContent.trim();
    }

    const bikeData = {
      id: cardElement.dataset.productId,
      variantId: variantId,
      name: cardElement.dataset.productTitle,
      price: currentPrice / 100, // Convert from cents
      img: imgSrc,
      handle: cardElement.dataset.productHandle,
      variantName: variantName,
      shippingEta: shippingEta,
      addons: addons
    };

    // Store selected bike
    selectedBikes[bikeNum - 1] = bikeData;

    // Update card visual state
    const grid = cardElement.closest('.bogo-bike-grid');
    if (grid) {
      grid.querySelectorAll('product-card').forEach(function(card) {
        card.classList.remove('selected');
        // Reset button text
        const cardBtn = card.querySelector('[data-action="add-to-bundle"] span:first-child');
        if (cardBtn) cardBtn.textContent = 'Add';
      });
      cardElement.classList.add('selected');
      // Update button text for selected card
      const selectedBtn = cardElement.querySelector('[data-action="add-to-bundle"] span:first-child');
      if (selectedBtn) selectedBtn.textContent = '✓ Selected';
    }

    // Update UI
    updateStickyBar();

    // Handle navigation with auto-scroll
    if (bikeNum === 1) {
      if (selectedBikes[1]) {
        showBikeConfirmation();
        setTimeout(function() { scrollToSection('#bike-confirmation'); }, 150);
      } else {
        setTimeout(function() {
          bogoGoToStep(2);
        }, 300);
      }
    } else if (bikeNum === 2) {
      showBikeConfirmation();
      setTimeout(function() { scrollToSection('#bike-confirmation'); }, 150);
    }
  }

  // ===== ACCESSORY CARD SELECTION (for product-card based accessories) =====
  function handleAccessoryCardSelection(cardElement, btn) {
    // Get accessory data from product-card
    const variantId = btn.dataset.id || cardElement.dataset.productId;
    const accessoryData = {
      id: variantId,
      productId: cardElement.dataset.productId,
      name: cardElement.dataset.productTitle,
      price: parseInt(cardElement.dataset.productPrice) / 100, // Convert from cents
      img: cardElement.dataset.productImage,
      quantity: 1
    };

    // Check if already selected
    const index = selectedAccessories.findIndex(function(a) {
      return a.id === accessoryData.id;
    });

    const btnText = btn.querySelector('span:first-child');
    const quantityComponent = cardElement.querySelector('.quantity--component');

    if (index > -1) {
      // Remove accessory
      selectedAccessories.splice(index, 1);
      cardElement.classList.remove('selected');
      if (btnText) btnText.textContent = 'Add';
      btn.style.display = '';
      if (quantityComponent) {
        quantityComponent.classList.add('hidden');
        const qtyInput = quantityComponent.querySelector('.quantity--component__value');
        if (qtyInput) qtyInput.value = 0;
      }
    } else {
      // Add accessory
      selectedAccessories.push(accessoryData);
      cardElement.classList.add('selected');
      if (btnText) btnText.textContent = '✓ Added';

      // Show quantity component if available (for non-highlight cards)
      if (quantityComponent) {
        quantityComponent.classList.remove('hidden');
        const qtyInput = quantityComponent.querySelector('.quantity--component__value');
        if (qtyInput) qtyInput.value = 1;
      }
    }

    updateStickyBar();
  }

  // ===== QUANTITY BUTTON HANDLERS =====
  function initQuantityButtons() {
    document.addEventListener('click', function(e) {
      // Handle increase quantity
      const increaseBtn = e.target.closest('[data-action="bundle-increase-quantity"]');
      if (increaseBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleQuantityChange(increaseBtn, 1);
        return;
      }

      // Handle decrease quantity
      const decreaseBtn = e.target.closest('[data-action="bundle-decrease-quantity"]');
      if (decreaseBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleQuantityChange(decreaseBtn, -1);
        return;
      }
    });
  }

  function handleQuantityChange(btn, delta) {
    const quantityComponent = btn.closest('.quantity--component');
    const productCard = btn.closest('product-card');

    if (!quantityComponent || !productCard) return;

    const qtyInput = quantityComponent.querySelector('.quantity--component__value');
    if (!qtyInput) return;

    let currentQty = parseInt(qtyInput.value) || 0;
    let newQty = Math.max(0, currentQty + delta);

    qtyInput.value = newQty;

    // Get variant ID for this product
    const addBtn = productCard.querySelector('[data-action="add-to-bundle"]');
    const variantId = addBtn ? addBtn.dataset.id : productCard.dataset.productId;

    // Find accessory in selectedAccessories
    const index = selectedAccessories.findIndex(function(a) {
      return a.id === variantId;
    });

    if (newQty === 0) {
      // Remove from selection
      if (index > -1) {
        selectedAccessories.splice(index, 1);
      }
      productCard.classList.remove('selected');
      quantityComponent.classList.add('hidden');
      if (addBtn) {
        addBtn.style.display = '';
        const btnText = addBtn.querySelector('span:first-child');
        if (btnText) btnText.textContent = 'Add';
      }
    } else {
      // Update quantity in selection
      if (index > -1) {
        selectedAccessories[index].quantity = newQty;
      } else {
        // Add new if not found (shouldn't normally happen)
        selectedAccessories.push({
          id: variantId,
          productId: productCard.dataset.productId,
          name: productCard.dataset.productTitle,
          price: parseInt(productCard.dataset.productPrice) / 100,
          img: productCard.dataset.productImage,
          quantity: newQty
        });
        productCard.classList.add('selected');
      }
    }

    updateStickyBar();
    updateCheckoutSummary();
  }

  // ===== ACCESSORY BUTTONS (legacy - for old accessory card structure) =====
  function initAccessoryButtons() {
    // Use event delegation for accessory buttons
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[data-action="toggle-accessory"]');
      if (btn) {
        e.preventDefault();
        const card = btn.closest('.bogo-accessory-card');
        if (card) {
          handleAccessoryToggle(card, btn);
        }
      }
    });
  }

  function handleAccessoryToggle(card, btn) {
    const accessoryData = {
      id: card.dataset.accessoryId,
      name: card.dataset.accessoryName,
      price: parseInt(card.dataset.accessoryPrice) / 100 // Convert from cents
    };

    const index = selectedAccessories.findIndex(function(a) {
      return a.id === accessoryData.id;
    });

    if (index > -1) {
      // Remove
      selectedAccessories.splice(index, 1);
      card.classList.remove('selected');
    } else {
      // Add
      selectedAccessories.push(accessoryData);
      card.classList.add('selected');
    }

    updateStickyBar();
  }

  // ===== SCROLL HELPER =====
  function scrollToSection(selector) {
    var element = document.querySelector(selector);
    if (!element) return;

    // Use scrollIntoView + CSS scroll-margin-top (set in bogo-progress-bar snippet)
    // This avoids manual offset math that breaks when the bar transitions
    // between sticky (in-flow) and compact (fixed overlay) during smooth scroll.
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ===== BIKE SELECTION =====
  window.bogoSelectBike = function(bikeNum, cardElement) {
    const bikeData = {
      id: cardElement.dataset.bikeId,
      name: cardElement.dataset.bikeName,
      price: parseInt(cardElement.dataset.bikePrice),
      img: cardElement.dataset.bikeImg,
      handle: cardElement.dataset.bikeHandle
    };

    // Store selected bike
    selectedBikes[bikeNum - 1] = bikeData;

    // Update card visual state
    const grid = document.getElementById('bike-grid-' + bikeNum);
    if (grid) {
      grid.querySelectorAll('.bogo-bike-card').forEach(card => {
        card.classList.remove('selected');
        card.querySelector('.bogo-bike-select-btn').textContent = 'Add';
      });
      cardElement.classList.add('selected');
      cardElement.querySelector('.bogo-bike-select-btn').textContent = '✓ Selected';
    }

    // Update UI
    updateStickyBar();

    // Handle navigation
    if (bikeNum === 1) {
      // If second bike already selected, show confirmation
      if (selectedBikes[1]) {
        showBikeConfirmation();
      } else {
        // Auto-advance to step 2
        setTimeout(function() {
          bogoGoToStep(2);
        }, 300);
      }
    } else if (bikeNum === 2) {
      // Both bikes selected - show confirmation
      showBikeConfirmation();
    }
  };

  // ===== BIKE CONFIRMATION =====
  function showBikeConfirmation() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;

    // Determine which is paid vs free based on price
    let paidBike, freeBike, paidIndex, freeIndex;

    if (selectedBikes[0].price >= selectedBikes[1].price) {
      paidBike = selectedBikes[0];
      freeBike = selectedBikes[1];
      paidIndex = 1;
      freeIndex = 2;
    } else {
      paidBike = selectedBikes[1];
      freeBike = selectedBikes[0];
      paidIndex = 2;
      freeIndex = 1;
    }

    // Hide step 2 content and show confirmation
    const step2Section = document.getElementById('bogo-step-2');
    const bikeGrid2 = document.getElementById('bike-grid-2');
    const firstBikeBanner = document.getElementById('first-bike-banner');
    const confirmation = document.getElementById('bike-confirmation');

    if (bikeGrid2) bikeGrid2.style.display = 'none';
    if (firstBikeBanner) firstBikeBanner.style.display = 'none';
    if (confirmation) {
      confirmation.style.display = 'block';

      // Update confirmation panel
      document.getElementById('confirm-bike-1-img').src = paidBike.img;
      document.getElementById('confirm-bike-1-name').textContent = paidBike.name;
      document.getElementById('confirm-bike-1-price').textContent = '$' + paidBike.price.toLocaleString();
      document.getElementById('confirm-badge-1').textContent = 'YOU PAY';
      document.getElementById('confirm-badge-1').className = 'bogo-confirm-badge paid';

      document.getElementById('confirm-bike-2-img').src = freeBike.img;
      document.getElementById('confirm-bike-2-name').textContent = freeBike.name;
      document.getElementById('confirm-bike-2-price').textContent = '$' + freeBike.price.toLocaleString();
      document.getElementById('confirm-badge-2').textContent = 'FREE! 🎁';
      document.getElementById('confirm-badge-2').className = 'bogo-confirm-badge free';

      document.getElementById('confirm-savings').textContent = '$' + freeBike.price.toLocaleString();
    }

    // Update progress
    updateProgressBar();
  }

  window.bogoChangeBike = function(bikeNum) {
    // Hide confirmation, show bike grid
    const confirmation = document.getElementById('bike-confirmation');
    const bikeGrid2 = document.getElementById('bike-grid-2');
    const firstBikeBanner = document.getElementById('first-bike-banner');

    if (confirmation) confirmation.style.display = 'none';

    // Clear only the selected bike
    selectedBikes[bikeNum - 1] = null;

    // Clear visual selection - works with both old and new card structures
    const grid = document.getElementById('bike-grid-' + bikeNum);
    if (grid) {
      // Handle product-card elements (new structure)
      grid.querySelectorAll('product-card').forEach(function(card) {
        card.classList.remove('selected');
        const btn = card.querySelector('[data-action="add-to-bundle"] span:first-child');
        if (btn) btn.textContent = 'Add';
      });
      // Handle old .bogo-bike-card elements (legacy support)
      grid.querySelectorAll('.bogo-bike-card').forEach(function(card) {
        card.classList.remove('selected');
        const btn = card.querySelector('.bogo-bike-select-btn');
        if (btn) btn.textContent = 'Add';
      });
    }

    // Navigate to appropriate step
    if (bikeNum === 1) {
      bogoGoToStep(1);
    } else {
      bogoGoToStep(2);
      if (bikeGrid2) bikeGrid2.style.display = 'grid';
      if (firstBikeBanner && selectedBikes[0]) {
        firstBikeBanner.style.display = 'block';
        document.getElementById('selected-bike-1-img').src = selectedBikes[0].img;
        document.getElementById('selected-bike-1-name').textContent = selectedBikes[0].name;
        document.getElementById('selected-bike-1-price').textContent = '$' + selectedBikes[0].price.toLocaleString();
      }
    }

    updateStickyBar();
  };

  // ===== ACCESSORIES =====
  // (Accessory toggle is now handled by initAccessoryButtons and handleAccessoryToggle)

  // ===== PROTECTION PLANS =====
  function initProtectionCheckboxes() {
    document.querySelectorAll('.bogo-protection-check').forEach(function(checkbox) {
      if (checkbox.disabled) return; // Skip disabled checkboxes

      checkbox.addEventListener('change', function() {
        const type = this.dataset.type;
        const bike = this.dataset.bike;
        const price = parseInt(this.dataset.price) || 0;
        const variantId = this.dataset.id; // Product variant ID

        if (!type || !bike) return;

        const key = type + '-' + bike;

        var name = this.dataset.name || type;

        if (this.checked) {
          selectedProtection.push({
            key: key,
            type: type,
            bike: bike,
            price: price,
            variantId: variantId,
            name: name
          });
        } else {
          selectedProtection = selectedProtection.filter(function(p) {
            return p.key !== key;
          });
        }

        updateStickyBar();
        updateCheckoutSummary();
      });
    });
  }

  // ===== NAVIGATION =====
  window.bogoNavigateToStep = function(step) {
    // Can always go back
    if (step < currentStep) {
      bogoGoToStep(step);
      return;
    }

    // Going forward - check requirements
    if (step === 2 && selectedBikes[0]) {
      bogoGoToStep(2);
    } else if (step === 3 && selectedBikes[0] && selectedBikes[1]) {
      bogoGoToStep(3);
    } else if (step === 4 && selectedBikes[0] && selectedBikes[1]) {
      bogoGoToStep(4);
    }
  };

  window.bogoGoToStep = function(step) {
    currentStep = step;

    // Update sections visibility
    document.querySelectorAll('.bogo-step-section').forEach(function(section) {
      section.classList.remove('active');
    });

    const targetSection = document.getElementById('bogo-step-' + step);
    if (targetSection) {
      targetSection.classList.add('active');
    }

    // Always hide confirmation panel and first-bike-banner when changing steps
    // (they are outside .bogo-step-section so not controlled by active class)
    const confirmation = document.getElementById('bike-confirmation');
    const firstBikeBanner = document.getElementById('first-bike-banner');
    if (confirmation) confirmation.style.display = 'none';
    if (firstBikeBanner) firstBikeBanner.style.display = 'none';

    // Hide hero + intro sections on step 3+
    const heroSection = document.getElementById('shopify-section-hero_valentines');
    const stepsSection = document.getElementById('shopify-section-text_with_icons_steps');
    if (step >= 3) {
      if (heroSection) heroSection.style.display = 'none';
      if (stepsSection) stepsSection.style.display = 'none';
    } else {
      if (heroSection) heroSection.style.display = '';
      if (stepsSection) stepsSection.style.display = '';
    }

    // Handle special cases
    if (step === 2) {
      // Show first bike banner
      if (selectedBikes[0] && firstBikeBanner) {
        firstBikeBanner.style.display = 'block';
        document.getElementById('selected-bike-1-img').src = selectedBikes[0].img;
        document.getElementById('selected-bike-1-name').textContent = selectedBikes[0].name;
        document.getElementById('selected-bike-1-price').textContent = '$' + selectedBikes[0].price.toLocaleString();
      }

      const bikeGrid2 = document.getElementById('bike-grid-2');
      if (bikeGrid2) bikeGrid2.style.display = 'grid';
    }

    // Hide/show progress bar
    const progressBar = document.getElementById('bogo-progress-bar');
    if (step === 4) {
      updateCheckoutBikeCards();
      updateCheckoutSummary();
      if (progressBar) progressBar.style.display = 'none';
    } else {
      if (progressBar) progressBar.style.display = '';
    }

    const stickyBar = document.getElementById('bogo-checkout-bar');
    if (stickyBar) stickyBar.style.display = step === 3 ? 'none' : 'block';

    updateProgressBar();
    updateStickyBar();

    // Auto-scroll to the current step section
    const stepSelectors = {
      1: '#shopify-section-bogo_bike_selection',
      2: '#first-bike-banner',
      3: '#bogo-step-3',
      4: '#bogo-step-4'
    };
    if (stepSelectors[step]) {
      if (step === 3) {
        // Step 3 has a big layout shift (steps 1+2 collapse).
        // Use manual scroll so we scroll far enough past the sentinel
        // to trigger the compact glass pill, then position heading below it.
        setTimeout(function() {
          scrollToSection(stepSelectors[3]);
        }, 150);
      } else if (step === 4) {
        // Step 4 hides the progress bar, scroll to the checkout section
        setTimeout(function() {
          scrollToSection(stepSelectors[4]);
        }, 150);
      } else {
        requestAnimationFrame(function() {
          setTimeout(function() {
            scrollToSection(stepSelectors[step]);
          }, 50);
        });
      }
    }
  };

  window.bogoNextStep = function() {
    if (currentStep < 4) {
      bogoGoToStep(currentStep + 1);
    }
  };

  // ===== PROGRESS BAR =====
  function updateProgressBar() {
    var desktop = document.querySelector('.bogo-progress-desktop');
    var mobile = document.querySelector('.bogo-progress-mobile');

    // === Desktop: 4-step bar (1:1 mapping with internal steps) ===
    if (desktop) {
      desktop.querySelectorAll('.bogo-step-circle').forEach(function(circle, index) {
        circle.classList.remove('active', 'completed');
        var stepNum = index + 1;
        if (stepNum < currentStep) {
          circle.classList.add('completed');
          circle.innerHTML = '';
        } else if (stepNum === currentStep) {
          circle.classList.add('active');
          circle.textContent = stepNum;
        } else {
          circle.textContent = stepNum;
        }
      });

      desktop.querySelectorAll('.bogo-step-label').forEach(function(label, index) {
        label.classList.remove('active');
        if (index + 1 === currentStep) {
          label.classList.add('active');
        }
      });

      desktop.querySelectorAll('.bogo-progress-step').forEach(function(step, index) {
        step.classList.remove('clickable');
        if (index + 1 <= currentStep) {
          step.classList.add('clickable');
        }
      });

      for (var i = 1; i <= 3; i++) {
        var line = document.getElementById('progress-line-' + i);
        if (line) {
          line.style.width = i < currentStep ? '100%' : '0%';
        }
      }
    }

    // === Mobile: 3-step bar ===
    // Mapping: mobile step 1 = internal steps 1+2, mobile step 2 = internal step 3, mobile step 3 = internal step 4
    if (mobile) {
      var mobileStep;
      if (currentStep <= 2) mobileStep = 1;
      else if (currentStep === 3) mobileStep = 2;
      else mobileStep = 3;

      mobile.querySelectorAll('.mobile-step').forEach(function(step) {
        var ms = parseInt(step.dataset.mobileStep);
        var circle = step.querySelector('.bogo-step-circle');
        var label = step.querySelector('.bogo-step-label');

        circle.classList.remove('active', 'completed');
        label.classList.remove('active');
        step.classList.remove('clickable');

        if (ms < mobileStep) {
          circle.classList.add('completed');
          circle.innerHTML = '';
          step.classList.add('clickable');
        } else if (ms === mobileStep) {
          circle.classList.add('active');
          circle.textContent = ms;
          label.classList.add('active');
          step.classList.add('clickable');
        } else {
          circle.textContent = ms;
        }
      });

      // Mobile progress lines
      var mobileLine1 = document.getElementById('mobile-progress-line-1');
      var mobileLine2 = document.getElementById('mobile-progress-line-2');
      if (mobileLine1) mobileLine1.style.width = mobileStep > 1 ? '100%' : (currentStep === 2 ? '50%' : '0%');
      if (mobileLine2) mobileLine2.style.width = mobileStep > 2 ? '100%' : '0%';
    }
  }

  // ===== STICKY BAR =====
  function updateStickyBar() {
    const bikeCount = selectedBikes.filter(function(b) { return b !== null; }).length;

    // Update slots
    const slot1 = document.getElementById('bar-slot-1');
    const slot2 = document.getElementById('bar-slot-2');

    if (slot1) {
      if (selectedBikes[0]) {
        slot1.classList.add('filled');
        slot1.innerHTML = '<img src="' + selectedBikes[0].img + '" alt="">';
      } else {
        slot1.classList.remove('filled');
        slot1.innerHTML = '+';
      }
    }

    if (slot2) {
      if (selectedBikes[1]) {
        slot2.classList.add('filled');
        slot2.innerHTML = '<img src="' + selectedBikes[1].img + '" alt="">';
      } else {
        slot2.classList.remove('filled');
        slot2.innerHTML = '+';
      }
    }

    // Update count
    const countEl = document.getElementById('bar-count');
    if (countEl) countEl.textContent = bikeCount;

    // Calculate totals
    let total = 0;
    let original = 0;

    if (selectedBikes[0] && selectedBikes[1]) {
      const bike1 = selectedBikes[0];
      const bike2 = selectedBikes[1];
      const paidBike = bike1.price >= bike2.price ? bike1 : bike2;

      total = paidBike.price;
      original = bike1.price + bike2.price;
    } else if (selectedBikes[0]) {
      total = selectedBikes[0].price;
      original = selectedBikes[0].price;
    }

    // Add accessories (with quantity support)
    const accessoriesTotal = selectedAccessories.reduce(function(sum, a) {
      const qty = a.quantity || 1;
      return sum + (a.price * qty);
    }, 0);
    total += accessoriesTotal;

    // Add protection
    const protectionTotal = selectedProtection.reduce(function(sum, p) {
      return sum + p.price;
    }, 0);
    total += protectionTotal;

    // Update display
    const totalEl = document.getElementById('bar-total');
    const originalEl = document.getElementById('bar-original');

    if (totalEl) totalEl.textContent = '$' + total.toLocaleString();
    if (originalEl) originalEl.textContent = original > 0 && original !== total ? '$' + original.toLocaleString() : '';

    // Update button
    const btn = document.getElementById('bar-btn');
    if (btn) {
      btn.classList.remove('secondary', 'checkout');

      if (currentStep === 1) {
        btn.onclick = function() { bogoNextStep(); };
        if (selectedBikes[0]) {
          btn.disabled = false;
          btn.textContent = 'Continue to Next Step →';
        } else {
          btn.disabled = true;
          btn.textContent = 'Select Your eBike';
        }
      } else if (currentStep === 2) {
        btn.onclick = function() { bogoNextStep(); };
        if (selectedBikes[1]) {
          btn.disabled = false;
          btn.textContent = 'Continue to Next Step →';
        } else {
          btn.disabled = true;
          btn.textContent = 'Select 2nd eBike';
        }
      } else if (currentStep === 3) {
        btn.onclick = function() { bogoNextStep(); };
        btn.disabled = false;
        btn.textContent = 'Continue to Checkout →';
      } else if (currentStep === 4) {
        btn.onclick = function() { bogoCompletePurchase(); };
        btn.disabled = false;
        btn.textContent = 'Complete Purchase →';
        btn.classList.add('checkout');
      }
    }
  }

  // ===== CHECKOUT =====
  function filterUpgradesForBike(bikeNum, bikeHandle) {
    var section = document.querySelector('.bogo-checkout-bike-upgrades[data-bike="' + bikeNum + '"]');
    if (!section) return;

    var handle = (bikeHandle || '').toLowerCase().trim();
    console.log('[BOGO] filterUpgradesForBike bike=' + bikeNum + ', handle="' + handle + '"');

    // If no handle available, show all upgrades (don't filter)
    if (!handle) {
      section.querySelectorAll('.bogo-upgrade-option').forEach(function(option) {
        option.style.display = '';
      });
      return;
    }

    section.querySelectorAll('.bogo-upgrade-option').forEach(function(option) {
      var allowed = (option.dataset.allowedBikes || '').trim();
      console.log('[BOGO]   upgrade allowed="' + allowed + '"');

      // Empty = show for all bikes
      if (!allowed) {
        option.style.display = '';
        return;
      }

      // Exact-match the selected bike's handle against the allowed list.
      // Substring matching is unsafe: "juggernaut-lite-plus-2" would falsely match "juggernaut-lite".
      var handles = allowed.split(',').map(function(h) { return h.trim().toLowerCase(); });
      var matched = handles.indexOf(handle) > -1;
      console.log('[BOGO]   match result:', matched, 'bike="' + handle + '" vs allowed=' + JSON.stringify(handles));
      if (matched) {
        option.style.display = '';
      } else {
        option.style.display = 'none';
        // Uncheck if hidden
        var checkbox = option.querySelector('.bogo-protection-check');
        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event('change'));
        }
      }
    });
  }

  function updateCheckoutBikeCards() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;

    let paidBike, freeBike;

    if (selectedBikes[0].price >= selectedBikes[1].price) {
      paidBike = selectedBikes[0];
      freeBike = selectedBikes[1];
    } else {
      paidBike = selectedBikes[1];
      freeBike = selectedBikes[0];
    }

    // Update paid bike card
    const bike1Name = document.getElementById('checkout-bike1-name');
    const bike1Price = document.getElementById('checkout-bike1-price');
    const bike1Img = document.getElementById('checkout-bike1-img');
    const bike1Variant = document.getElementById('checkout-bike1-variant');

    if (bike1Name) bike1Name.textContent = paidBike.name;
    if (bike1Price) bike1Price.textContent = '$' + paidBike.price.toLocaleString();
    if (bike1Img) {
      bike1Img.src = paidBike.img;
      bike1Img.alt = paidBike.name;
    }
    if (bike1Variant) bike1Variant.textContent = paidBike.variantName || '';

    var bike1Shipping = document.getElementById('checkout-bike1-shipping');
    if (bike1Shipping) {
      if (paidBike.shippingEta) {
        bike1Shipping.innerHTML = '<i class="ph ph-truck"></i> ' + paidBike.shippingEta;
      } else {
        bike1Shipping.textContent = '';
      }
    }

    // Debug: show bike handle in UI
    var bike1HandleEl = document.getElementById('checkout-bike1-handle');
    if (bike1HandleEl) bike1HandleEl.textContent = 'Handle: ' + (paidBike.handle || 'UNDEFINED');

    // Update free bike card
    const bike2Name = document.getElementById('checkout-bike2-name');
    const bike2Original = document.getElementById('checkout-bike2-original');
    const bike2Img = document.getElementById('checkout-bike2-img');
    const bike2Variant = document.getElementById('checkout-bike2-variant');

    if (bike2Name) bike2Name.textContent = freeBike.name;
    if (bike2Original) bike2Original.textContent = '$' + freeBike.price.toLocaleString();
    if (bike2Img) {
      bike2Img.src = freeBike.img;
      bike2Img.alt = freeBike.name;
    }
    if (bike2Variant) bike2Variant.textContent = freeBike.variantName || '';

    var bike2Shipping = document.getElementById('checkout-bike2-shipping');
    if (bike2Shipping) {
      if (freeBike.shippingEta) {
        bike2Shipping.innerHTML = '<i class="ph ph-truck"></i> ' + freeBike.shippingEta;
      } else {
        bike2Shipping.textContent = '';
      }
    }

    // Filter upgrade options based on selected bike handles
    filterUpgradesForBike('1', paidBike.handle);
    filterUpgradesForBike('2', freeBike.handle);

    // Update accessories section
    const accSection = document.getElementById('checkout-accessories');
    const accList = document.getElementById('checkout-accessories-list');

    if (accSection && accList) {
      if (selectedAccessories.length > 0) {
        accSection.style.display = 'block';
        let accHtml = '';
        selectedAccessories.forEach(function(acc) {
          const qty = acc.quantity || 1;
          const itemTotal = acc.price * qty;
          const qtyDisplay = qty > 1 ? ' × ' + qty : '';
          accHtml += '<div class="bogo-checkout-accessory-item">' +
            '<span>' + acc.name + qtyDisplay + '</span>' +
            '<span>$' + itemTotal.toLocaleString() + '</span>' +
            '</div>';
        });
        accList.innerHTML = accHtml;
      } else {
        accSection.style.display = 'none';
      }
    }
  }

  function updateCheckoutSummary() {
    if (!selectedBikes[0] || !selectedBikes[1]) return;

    let paidBike, freeBike;

    if (selectedBikes[0].price >= selectedBikes[1].price) {
      paidBike = selectedBikes[0];
      freeBike = selectedBikes[1];
    } else {
      paidBike = selectedBikes[1];
      freeBike = selectedBikes[0];
    }

    const original = selectedBikes[0].price + selectedBikes[1].price;
    const savings = freeBike.price;
    let total = paidBike.price;

    // Add accessories (with quantity support)
    const accessoriesTotal = selectedAccessories.reduce(function(sum, a) {
      const qty = a.quantity || 1;
      return sum + (a.price * qty);
    }, 0);
    total += accessoriesTotal;

    // Add protection
    const protectionTotal = selectedProtection.reduce(function(sum, p) {
      return sum + p.price;
    }, 0);
    total += protectionTotal;

    // Update display
    const subtotalEl = document.getElementById('checkout-subtotal');
    const savingsEl = document.getElementById('checkout-savings');
    const protectionRow = document.getElementById('protection-row');
    const protectionEl = document.getElementById('checkout-protection');
    const accessoriesRow = document.getElementById('accessories-row');
    const accessoriesTotalEl = document.getElementById('checkout-accessories-total');
    const totalEl = document.getElementById('checkout-total');

    if (subtotalEl) subtotalEl.textContent = '$' + original.toLocaleString();
    if (savingsEl) savingsEl.textContent = '-$' + savings.toLocaleString();

    // Populate individual protection/upgrade line items
    var protectionSection = document.getElementById('checkout-protection-section');
    var protectionList = document.getElementById('checkout-protection-list');
    if (protectionSection && protectionList) {
      if (selectedProtection.length > 0) {
        protectionSection.style.display = 'block';
        var protHtml = '';
        selectedProtection.forEach(function(p) {
          var bikeLabel = p.bike === '1' ? 'Bike 1' : 'Bike 2';
          protHtml += '<div class="bogo-checkout-accessory-item">' +
            '<span>' + p.name + ' <small style="color:#888;">(' + bikeLabel + ')</small></span>' +
            '<span>$' + p.price.toLocaleString() + '</span>' +
            '</div>';
        });
        protectionList.innerHTML = protHtml;
      } else {
        protectionSection.style.display = 'none';
      }
    }

    if (protectionRow && protectionEl) {
      if (protectionTotal > 0) {
        protectionRow.style.display = 'flex';
        protectionEl.textContent = '+$' + protectionTotal.toLocaleString();
      } else {
        protectionRow.style.display = 'none';
      }
    }

    if (accessoriesRow && accessoriesTotalEl) {
      if (accessoriesTotal > 0) {
        accessoriesRow.style.display = 'flex';
        accessoriesTotalEl.textContent = '+$' + accessoriesTotal.toLocaleString();
      } else {
        accessoriesRow.style.display = 'none';
      }
    }

    if (totalEl) totalEl.textContent = '$' + total.toLocaleString() + ' CAD';
  }

  // ===== COMPLETE PURCHASE =====
  window.bogoCompletePurchase = function() {
    // Determine which bike is paid vs free
    let paidBike, freeBike;
    if (selectedBikes[0] && selectedBikes[1]) {
      if (selectedBikes[0].price >= selectedBikes[1].price) {
        paidBike = selectedBikes[0];
        freeBike = selectedBikes[1];
      } else {
        paidBike = selectedBikes[1];
        freeBike = selectedBikes[0];
      }
    }

    // Build cart data
    const cartItems = [];

    // Add bikes with their variant IDs
    selectedBikes.forEach(function(bike, index) {
      if (bike) {
        const isFree = freeBike && bike.variantId === freeBike.variantId;
        const bikeKey = 1000000000 + Math.floor(Math.random() * 9000000000);
        cartItems.push({
          id: bike.variantId || bike.id,
          quantity: 1,
          properties: Object.assign({
            '_bogo': 'true',
            '_bike_number': (index + 1).toString(),
            '_bogo_free': isFree ? 'true' : 'false',
            '_key': bikeKey.toString()
          }, bike.shippingEta ? { 'Estimated Delivery': bike.shippingEta } : {})
        });

        // Add bike addons (BiktrixCare+, Theftcare from card selection)
        if (bike.addons && bike.addons.length > 0) {
          bike.addons.forEach(function(addon) {
            if (addon.id) {
              cartItems.push({
                id: addon.id,
                quantity: 1,
                properties: {
                  '_bogo_addon': 'true',
                  '_for_bike': (index + 1).toString()
                }
              });
            }
          });
        }
      }
    });

    // Add accessories with quantities
    selectedAccessories.forEach(function(acc) {
      cartItems.push({
        id: acc.id,
        quantity: acc.quantity || 1,
        properties: {
          '_bogo_accessory': 'true'
        }
      });
    });

    // Add protection plans and upgrades from checkout
    selectedProtection.forEach(function(protection) {
      if (protection.variantId) {
        cartItems.push({
          id: protection.variantId,
          quantity: 1,
          properties: {
            '_bogo_protection': 'true',
            '_protection_type': protection.type,
            '_for_bike': protection.bike
          }
        });
      }
    });

    // Log for development
    console.log('BOGO Cart Items:', cartItems);

    // Show loading state
    var ctaBtn = document.querySelector('.bogo-checkout-cta-btn');
    if (ctaBtn) {
      ctaBtn.disabled = true;
      ctaBtn.setAttribute('data-original-text', ctaBtn.textContent);
      ctaBtn.innerHTML = '<span class="bogo-spinner"></span> Processing...';
    }

    // Add to Shopify cart using AJAX API
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }
      return response.json();
    })
    .then(function(data) {
      console.log('Added to cart:', data);
      if (ctaBtn) ctaBtn.innerHTML = '<span class="bogo-spinner"></span> Redirecting...';
      // Redirect to checkout
      window.location.href = '/checkout';
    })
    .catch(function(error) {
      console.error('Cart error:', error);
      if (ctaBtn) {
        ctaBtn.disabled = false;
        ctaBtn.textContent = ctaBtn.getAttribute('data-original-text') || 'Complete Purchase →';
      }
      alert('There was an error adding items to cart. Please try again.');
    });
  };

  // ===== INFO MODAL =====
  window.bogoOpenInfoModal = function(btn) {
    var modal = document.getElementById('bogo-info-modal');
    var titleEl = document.getElementById('bogo-info-modal-title');
    var bodyEl = document.getElementById('bogo-info-modal-body');
    if (!modal || !titleEl || !bodyEl) return;

    titleEl.textContent = btn.dataset.infoTitle || '';

    // If info_page handle is set, fetch content from that Shopify page
    var pageHandle = (btn.dataset.infoPage || '').trim();
    if (pageHandle) {
      bodyEl.innerHTML = '<p style="text-align:center;padding:20px;">Loading...</p>';
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.setAttribute('modal-overlay-open', 'true');

      fetch('/pages/' + pageHandle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          bodyEl.innerHTML = data.page.body_html;
        })
        .catch(function() {
          bodyEl.innerHTML = btn.dataset.infoContent || '<p>Could not load info.</p>';
        });
    } else {
      bodyEl.innerHTML = btn.dataset.infoContent || '';
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.setAttribute('modal-overlay-open', 'true');
    }
  };

  window.bogoCloseInfoModal = function() {
    var modal = document.getElementById('bogo-info-modal');
    if (modal) modal.setAttribute('aria-hidden', 'true');
    document.documentElement.setAttribute('modal-overlay-open', 'false');
  };

  // Close modal on overlay click
  document.addEventListener('click', function(e) {
    var modal = document.getElementById('bogo-info-modal');
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    // Click on the dialog backdrop (the ::before overlay) means the target is the dialog itself
    if (e.target === modal) {
      bogoCloseInfoModal();
    }
  });

})();
